import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Brain, Download, Lock, Shield, Sparkles } from "lucide-react";
import { requireSuperAdminGuard } from "@/lib/security/tenantIsolation";
import {
  STRATEGIC_DOMAINS,
  askStrategicAi,
  exportTextFile,
  getStrategicAiSettings,
  isStrategicUnlocked,
  lockStrategic,
  unlockStrategic,
  type StrategicDomain,
} from "@/lib/ai/strategicAi";
import { trackEvent } from "@/lib/audit";

export const Route = createFileRoute("/admin/strategic-ai")({
  beforeLoad: ({ location }) => requireSuperAdminGuard(location.pathname),
  component: StrategicAiPage,
});

const COMMAND_MODULES = [
  "Executive Copilot",
  "Business Intelligence",
  "Market Intelligence",
  "Advertising Intelligence",
  "Consumer Intelligence",
  "Sports Intelligence",
  "Sponsor Intelligence",
  "Financial Modeling",
  "Proposal Generator",
  "Contract Generator",
  "Presentation Generator",
  "Survey Intelligence",
  "Audience Analytics",
  "Competitive Intelligence",
  "Strategic Recommendations",
  "Task Automation",
  "Report Center",
  "Data Export Center",
  "Governance & Audit",
  "AI Settings",
];

function StrategicAiPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [domain, setDomain] = useState<StrategicDomain>("business");
  const [question, setQuestion] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const settings = useMemo(getStrategicAiSettings, []);

  useEffect(() => {
    setUnlocked(isStrategicUnlocked());
  }, []);

  function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (unlockStrategic(pin)) {
      setUnlocked(true);
      setPinError(null);
      void trackEvent("super_admin_access_verified", { area: "strategic_ai" });
    } else {
      setPinError("PIN incorrecto.");
    }
  }

  async function handleAsk() {
    if (!question.trim() || loading) return;
    setLoading(true);
    setError(null);
    setOutput("");
    let contextHint: string | undefined;
    try {
      const { buildStrategicAiContext, seedDemoResponsesIfEmpty } = await import("@/lib/polls/surveyIntelligence");
      seedDemoResponsesIfEmpty();
      contextHint = buildStrategicAiContext();
    } catch { /* noop */ }
    const res = await askStrategicAi({ domain, question, contextHint });
    setLoading(false);
    if (!res.ok) {
      setError(res.error ?? "Error");
      return;
    }
    setOutput(res.content ?? "");
    void trackEvent("strategic_ai_report_generated", { domain, len: (res.content ?? "").length });
  }

  function handleExport(kind: "md" | "txt") {
    if (!output) return;
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `strategic-${domain}-${ts}.${kind}`;
    exportTextFile(filename, output, kind === "md" ? "text/markdown" : "text/plain");
    void trackEvent("strategic_ai_document_generated", { domain, kind });
  }

  if (!unlocked) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6">
        <form onSubmit={handleUnlock} className="w-full rounded-2xl glass p-6">
          <div className="mb-3 flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-black">Strategic AI · Super Admin</h1>
          </div>
          <p className="mb-4 text-[12px] text-muted-foreground">Acceso restringido. Ingrese PIN.</p>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="PIN"
            className="w-full rounded-md bg-background/60 px-3 py-2 text-sm border border-border/40"
          />
          {pinError && <div className="mt-2 text-[11px] text-red-400">{pinError}</div>}
          <button type="submit" className="mt-3 w-full rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground">
            Desbloquear
          </button>
          <Link to="/admin" className="mt-3 block text-center text-[11px] text-muted-foreground hover:text-foreground">
            <ArrowLeft className="mr-1 inline h-3 w-3" /> Volver a Admin
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <header className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-black">Strategic AI · Command Center</h1>
          <span className="rounded bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">SUPER ADMIN</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { lockStrategic(); setUnlocked(false); }} className="text-[11px] text-muted-foreground hover:text-foreground">
            <Shield className="mr-1 inline h-3 w-3" /> Bloquear
          </button>
          <Link to="/admin" className="text-[11px] text-muted-foreground hover:text-foreground">
            <ArrowLeft className="mr-1 inline h-3 w-3" /> Admin
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
        <aside className="rounded-2xl glass p-3">
          <div className="mb-2 text-[11px] font-black text-muted-foreground">MÓDULOS</div>
          <ul className="space-y-1">
            {COMMAND_MODULES.map((m) => (
              <li key={m} className="rounded-md px-2 py-1.5 text-[12px] font-medium hover:bg-background/40 cursor-default">
                {m}
              </li>
            ))}
          </ul>
        </aside>

        <main className="space-y-3">
          <section className="rounded-2xl glass p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-black">
                <Sparkles className="h-4 w-4 text-primary" /> Executive Copilot
              </div>
              <div className="text-[10px] text-muted-foreground">
                Modelo: {settings.preferred_model} · Tokens: {settings.max_output_tokens}
              </div>
            </div>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {STRATEGIC_DOMAINS.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDomain(d.id)}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-bold ${domain === d.id ? "bg-primary text-primary-foreground" : "bg-background/40 text-muted-foreground hover:text-foreground"}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={4}
              placeholder="Pregunta estratégica, generar propuesta, analizar competidor, redactar contrato…"
              className="w-full rounded-md bg-background/60 px-3 py-2 text-sm border border-border/40"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={handleAsk}
                disabled={loading || !question.trim()}
                className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-bold text-primary-foreground disabled:opacity-40"
              >
                {loading ? "Procesando…" : "Ejecutar"}
              </button>
              {output && (
                <>
                  <button onClick={() => handleExport("md")} className="rounded-md bg-background/60 px-2 py-1.5 text-[11px] font-bold border border-border/40">
                    <Download className="mr-1 inline h-3 w-3" /> .md
                  </button>
                  <button onClick={() => handleExport("txt")} className="rounded-md bg-background/60 px-2 py-1.5 text-[11px] font-bold border border-border/40">
                    <Download className="mr-1 inline h-3 w-3" /> .txt
                  </button>
                </>
              )}
              {error && <span className="text-[11px] text-red-400">{error}</span>}
            </div>
          </section>

          {output && (
            <section className="rounded-2xl glass p-4">
              <div className="mb-2 text-[11px] font-black text-muted-foreground">RESPUESTA</div>
              <pre className="whitespace-pre-wrap text-[12px] leading-relaxed">{output}</pre>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}